import { Component, ChangeDetectionStrategy, input, output, signal, computed, effect, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-bottom-sheet',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bottom-sheet.component.html',
  styleUrl: './bottom-sheet.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BottomSheetComponent implements OnDestroy {
  open = input(false);
  title = input('');
  closed = output<void>();

  dragging = signal(false);
  dragY = signal(0);

  private startY = 0;
  private startTime = 0;
  private pointerId: number | null = null;

  backdropOpacity = computed(() => {
    const fade = Math.min(this.dragY() / 280, 1);
    return 0.58 * (1 - fade);
  });

  constructor() {
    effect(() => {
      document.body.classList.toggle('bottom-sheet-open', this.open());
      if (!this.open()) {
        this.dragY.set(0);
        this.dragging.set(false);
      }
    });
  }

  ngOnDestroy(): void {
    document.body.classList.remove('bottom-sheet-open');
    this.releasePointer();
  }

  close(): void {
    this.releasePointer();
    this.closed.emit();
  }

  onGrabDown(event: PointerEvent): void {
    this.pointerId = event.pointerId;
    this.startY = event.clientY;
    this.startTime = Date.now();
    this.dragging.set(true);
    this.dragY.set(0);
    (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
  }

  onGrabMove(event: PointerEvent): void {
    if (!this.dragging() || event.pointerId !== this.pointerId) return;
    this.dragY.set(Math.max(0, event.clientY - this.startY));
  }

  onGrabUp(event: PointerEvent): void {
    if (!this.dragging() || event.pointerId !== this.pointerId) return;
    const distance = this.dragY();
    const elapsed = Math.max(Date.now() - this.startTime, 1);
    const velocity = distance / elapsed;
    this.dragging.set(false);
    this.pointerId = null;
    if (distance > 88 || velocity > 0.55) {
      this.close();
      return;
    }
    this.dragY.set(0);
  }

  private releasePointer(): void {
    this.dragging.set(false);
    this.pointerId = null;
  }
}
